import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import AppEmbed, { type AppEmbedHandle, type ParsedNotificationItem } from '@/components/AppEmbed';
import { AppLauncherButton } from '@/components/apps/AppLauncherButton';
import { stripSessionParam, type AppManifestNavEntry, type AppMount } from '@/lib/apps-mounts';
import { isPublicAppPath } from '@/lib/app-public-paths';
import { useAppNavStore } from '@/stores/app-nav-store';
import { useAppNotificationsStore } from '@/stores/app-notifications-store';
import { useAuthStore } from '@/stores/auth-store';
import { setEmbedHandle, clearEmbedHandle } from '@/lib/app-embed-registry';
import { useSettings } from '@/hooks/useProducts';

interface AppMountedRouteProps {
  mount: AppMount;
}

/**
 * Renders an installed app under its mounted path prefix on the Rumio site
 * (contract v1.1 §1). Sits inside <PublicLayout>'s <Outlet>, so Rumio's header
 * + footer wrap the embedded iframe automatically.
 *
 * Path translation:
 *   - URL `/<prefix>` → app path = `mount.default_path`
 *   - URL `/<prefix>/foo/bar` → app path = `/foo/bar`
 *
 * URL sync:
 *   - Iframe-originated nav (rumio:location) → updates parent URL via React
 *     Router replace (no extra history entry; back button still works because
 *     React Router's history reflects each replace as the current location).
 *     We use `navigate(..., { replace: true })` so rapid in-app navigation
 *     doesn't bloat the back stack.
 *   - Parent-originated nav (popstate or programmatic Rumio navigation that
 *     lands on this prefix) → AppEmbed picks up the new `path` prop and
 *     forwards `rumio:navigate` to the iframe.
 */
export default function AppMountedRoute({ mount }: AppMountedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isInitialized } = useAuthStore();

  const prefixSegment = `/${mount.path_prefix}`;
  // The host URL's query string travels into the app with the path, so a link
  // like /onboarding?plan=growth&type=lodging reaches the app as intent it can
  // act on (the Site Builder preselects the plan and site type from it).
  // Without this the iframe was built from the pathname alone and anything a
  // host page put in the query was dropped at the mount. The session token is
  // never part of what is forwarded — AppEmbed adds a fresh one itself.
  const appPath = useMemo(() => {
    const remainder = location.pathname.slice(prefixSegment.length);
    const path =
      !remainder || remainder === '/'
        ? mount.default_path
        : remainder.startsWith('/') ? remainder : `/${remainder}`;
    return stripSessionParam(path + location.search);
  }, [location.pathname, location.search, prefixSegment, mount.default_path]);

  // The announced path is mirrored verbatim under the prefix. It may carry the
  // app's own query string (filter state, etc.), so the parent's current
  // `location.search` is NOT re-appended: doing so concatenated a stale
  // `?rumio_session=` onto every announcement and produced ever-growing
  // `?rumio_session=A?rumio_session=B…` host URLs. The token is stripped
  // defensively in case an older app build still echoes it.
  //
  // A signed-out visitor is only here because the app path is public (contract
  // v1.26 `public_paths`). When the app moves on to a path that is not, they
  // are sent to sign in and brought back to that page — pushed, not replaced,
  // so Back returns to the public page they came from. Without this the
  // mirrored URL would hit the gate below and land them on the marketing page,
  // losing what they clicked.
  const manifest = mount.app.manifest;
  const signedIn = Boolean(user);
  const handleLocationChange = useCallback(
    (newPath: string) => {
      const normalized = stripSessionParam(newPath.startsWith('/') ? newPath : `/${newPath}`);
      const target = `${prefixSegment}${normalized === '/' ? '' : normalized}`;
      if (!signedIn && !isPublicAppPath(normalized, manifest)) {
        navigate(`/login?redirect=${encodeURIComponent(target)}`);
        return;
      }
      if (target !== location.pathname + location.search + location.hash) {
        navigate(target, { replace: true });
      }
    },
    [navigate, prefixSegment, location.pathname, location.search, location.hash, signedIn, manifest]
  );

  // Launcher protocol (contract v1.1 §5.5). The button lives at host level
  // so `position: fixed` pins to the parent viewport — fixes the iframe
  // auto-resize collision where in-iframe `bottom: 6px` sat past the
  // visible viewport.
  const launcher = mount.app.manifest?.launcher;
  const embedRef = useRef<AppEmbedHandle | null>(null);
  const [launcherOpen, setLauncherOpen] = useState(false);

  // Contract v1.19: an app may ask for the host's chrome to go (`rumio:chrome`).
  // Honoured only on a minimal-chrome tenant — the social casinos, whose one
  // app is a game that owns the screen. Everywhere else the handler is absent
  // and the message does nothing. Reset whenever the mounted app changes.
  const { data: settingsData } = useSettings();
  const ownsScreen = (settingsData?.settings?.layout_chrome || 'default') === 'minimal';
  const [chromeHidden, setChromeHidden] = useState(false);
  useEffect(() => setChromeHidden(false), [mount.app.client_id]);
  const fullscreenApp = ownsScreen && chromeHidden;
  const handleLauncherToggle = useCallback(() => {
    embedRef.current?.toggleLauncher();
  }, []);

  // Contract v1.6 §3 — runtime nav contributed by the app via `rumio:nav`.
  // We forward into the shared store so Header (anywhere in the tree) can
  // render it, and we clear on unmount so a stale menu never leaks back to
  // the host shell once the user leaves the mount.
  const appId = mount.app.id;
  const setRuntimeNav = useAppNavStore((s) => s.setRuntimeNav);
  const clearRuntimeNav = useAppNavStore((s) => s.clearRuntimeNav);
  const handleNavContribute = useCallback(
    (items: AppManifestNavEntry[] | null) => {
      setRuntimeNav(appId, items);
    },
    [appId, setRuntimeNav],
  );
  useEffect(() => {
    return () => {
      clearRuntimeNav(appId);
    };
  }, [appId, clearRuntimeNav]);

  // Contract v1.10 — notifications published by the app via
  // `rumio:notifications`. Mirrors the nav-contribute pattern: write into a
  // shared store (read by the host NotificationBell), and clear on unmount.
  const setNotifs = useAppNotificationsStore((s) => s.setForApp);
  const clearNotifs = useAppNotificationsStore((s) => s.clearForApp);
  const appName = mount.app.name;
  const handleNotificationsContribute = useCallback(
    (payload: { items: ParsedNotificationItem[]; unreadCount: number } | null) => {
      if (payload === null) {
        clearNotifs(appId);
        return;
      }
      setNotifs({ appId, appName, unreadCount: payload.unreadCount, items: payload.items });
    },
    [appId, appName, setNotifs, clearNotifs],
  );
  useEffect(() => {
    return () => {
      clearNotifs(appId);
      clearEmbedHandle(appId);
    };
  }, [appId, clearNotifs]);

  // Bridge the imperative AppEmbed handle into a module-level registry so
  // the host NotificationBell (in the site header) can post action messages
  // back to the right iframe. See lib/app-embed-registry.ts.
  const registerEmbedHandle = useCallback(
    (handle: AppEmbedHandle | null) => {
      embedRef.current = handle;
      setEmbedHandle(appId, handle);
    },
    [appId],
  );

  // Gate the iframe behind LOGIN ONLY. A logged-out visitor would otherwise see
  // the app's own "sign in required" surface render inside the iframe —
  // confusing, and it reveals the embed mechanics — so we redirect them to the
  // host marketing page before the embed mounts. `manifest.marketing_path` lets
  // an app pin its own landing; otherwise we fall back to the site homepage. We
  // wait for `isInitialized` so an in-flight auth bootstrap doesn't
  // flash-redirect a signed-in user on hard reload.
  //
  // We deliberately do NOT gate on `user.hasActiveSubscription` here. That's
  // Rumio's notion of a *personal* subscription, but an app's entitlement can be
  // richer than that — e.g. the meal-planner grants access to an invited member
  // of someone else's subscribed household, who has no personal subscription.
  // Rumio can't know those rules; the app is the source of truth and enforces
  // them itself (per-request access checks + its own paywall surface). So any
  // authenticated user is allowed to mount the app, and the app decides whether
  // to show its content or its paywall.
  //
  // Contract v1.26: an app may list `public_paths` in its manifest. A
  // signed-out visitor on one of those (matched against the resolved app path,
  // so a bare `/<prefix>` is checked as `default_path`) gets the embed with an
  // anonymous session — `POST /apps/session` mints one with no `sub` — and the
  // app decides what an anonymous viewer sees. Every other path is gated as
  // before.
  if (!isInitialized) {
    return <div className="flex-1" aria-hidden />;
  }
  if (!user && !isPublicAppPath(appPath, mount.app.manifest)) {
    const manifestMarketingPath =
      typeof mount.app.manifest?.marketing_path === 'string'
        ? (mount.app.manifest.marketing_path as string)
        : null;
    return <Navigate to={manifestMarketingPath ?? '/'} replace />;
  }

  return (
    // Explicit `height` (not just `min-height`) so AppEmbed's `height: 100%`
    // resolves cleanly. With only min-height the parent's used height stays
    // auto/content-determined, so the iframe collapses to its default size
    // (~150px) and produces a tiny, scrolling embed.
    //
    // `dvh` (dynamic viewport height), not `vh`: on mobile `100vh` is the
    // *large* viewport — it includes the area behind the browser's address
    // bar — so a `vh`-sized iframe is taller than what's actually visible.
    // An embedded app's `position: fixed` overlay (modal/dialog) is pinned to
    // the iframe viewport, so its bottom (e.g. a dialog's action buttons)
    // ends up below the visible screen and can't be scrolled into view.
    // `dvh` tracks the visible viewport, keeping the iframe — and any fixed
    // overlay inside it — within reach.
    //
    // An app that asked for the screen (`fullscreenApp`) is laid over the whole
    // viewport, above the sticky header: on a phone held upright the game
    // turns itself a quarter turn, and a host header left across the top of
    // the glass would run sideways down one edge of it.
    <div
      className={fullscreenApp ? 'fixed inset-0 z-[60] flex flex-col bg-black' : 'flex-1 flex flex-col'}
      style={{ height: fullscreenApp ? '100dvh' : 'calc(100dvh - 4rem)' }}
      data-app-fullscreen={fullscreenApp ? 'true' : undefined}
    >
      <AppEmbed
        // Remount when the viewer signs in or out on a public path, so the app
        // gets a session for who is actually looking (anonymous vs. the user).
        key={user?.id ?? 'anonymous'}
        ref={registerEmbedHandle}
        appId={mount.app.client_id}
        baseUrl={mount.app.base_url}
        path={appPath}
        title={mount.label ?? mount.app.name}
        fillHeight
        onLocationChange={handleLocationChange}
        onLauncherStateChange={setLauncherOpen}
        onNavContribute={handleNavContribute}
        onNotificationsContribute={handleNotificationsContribute}
        onChromeChange={ownsScreen ? setChromeHidden : undefined}
        allowFullscreen={ownsScreen}
      />
      {launcher && (
        <AppLauncherButton
          launcher={launcher}
          appName={mount.app.name}
          onToggle={handleLauncherToggle}
          open={launcherOpen}
        />
      )}
    </div>
  );
}
